/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { withAppContext, getToken } from '@ctsu/loader';
import { GridTable, GridController } from '@ctsu/ctsu-mui-grid';
import {
  RenderCardHeader, RenderCardBody, RenderControl, RenderElement
} from 'components/RenderHelpers';
import ContentHeader from 'components/ContentHeader/ContentHeader';
import bindLogger from 'helpers/BindLogger';
import { faSyncAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  togglePacketLockBtn,
  getIndexing,
  getTrackingCodeStatus,
  clearTrackingCodeStatus,
  getPacketCommunication,
  setIndexing,
  toggleAdditionalDetails,
  saveEventHistory
} from 'actions/packet';
import { openConfirmation } from 'components/Confirmation/Confirmation';
import { ATCONSTANTS as ATC } from 'appconstants';
import { CanUserViewIndex, CanViewExternalFilters, CanUserViewDetails } from 'appconstants/Permission';
import {
  getQuery, usePathName, showSuccessNotification, getExportTitle
} from 'helpers';
import PacketBrowserColumns from './GridColumns';
import Filters from './PacketFilters';
import ExternalFilter from './ExternalFilters';
import Indexing from './Indexing';
import AdditionalDetails from './AdditionalDetails';

const controller = GridController.createController();
const EXTERNAL_FILTER_FIELDS = ['personCtepId', 'siteCtepId', 'protocolNumber'];

function PacketBrowser(props) {
  const location = useLocation();
  const query = getQuery(location);
  const packetId = query.get('packetid');
  const navigate = useNavigate();
  const pathname = usePathName(location);
  const {
    context, history, togglePacketLockBtn1, getPacketCommunication1, getIndexing1, saveEventHistory1, setIndexing1, clearTrackingCodeStatus1, toggleAdditionalDetails1, getTrackingCodeStatus1
  } = props;
  const { session } = context;
  let filterVal = {
    status: 'NOT COMPLETE'
  };
  if (packetId) {
    filterVal = {
      packetId
    };
  }
  const [appliedFilters, setAppliedfilters] = useState(filterVal);
  const [selectedPacket, setSelectedPacket] = useState(null);
  const [selectedIndex, setSelectedindex] = useState(0);
  const [prevSelectedIndex, setPrevSelectedIndex] = useState(0);
  const [metaData, setMetadata] = useState([]);
  const [allowEditing, setAllowEditing] = useState(false);
  const [page, setPage] = useState(0);
  const [isAditionalDetailsEdit, setAditionalDetailsEdit] = useState(false); // Additional details edditing
  const [isTakeBtn, setIsTakeBtn] = useState(false); // handle pagination issue on take click from page>1
  const [takeComm, setTakeComm] = useState(false);
  const [isChildEdit, setIsChildEdit] = useState(false);
  const [isChildAddEdit, setIsChildAddEdit] = useState(false);

  // packet filter
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [searchData, setSearchData] = useState({});

  useEffect(() => {
    if (packetId) {
      navigate(pathname);
    }
  }, [packetId, navigate, pathname]);

  const onClearExternalFilter = () => {
    setSelectedOptions([]);
    setSearchData({});
    EXTERNAL_FILTER_FIELDS.forEach((f) => {
      delete appliedFilters[f];
    });
    if (Object.keys(appliedFilters).length > 0) {
      controller.filterExternally(appliedFilters, true, false);
    } else {
      controller.filterExternally({}, false, true);
    }
  };

  const onSearch = () => {
    EXTERNAL_FILTER_FIELDS.forEach((f) => {
      delete appliedFilters[f];
    });
    if (searchData != null) {
      const keys = Object.keys(searchData);
      if (keys.length > 0) {
        keys.forEach((key) => {
          const value = searchData[key];
          if (value != null && value.trim().length > 0) {
            appliedFilters[key] = value;
          }
        });
      }
    }
    if (Object.keys(appliedFilters).length > 0) {
      controller.filterExternally(appliedFilters, false, false);
    } else {
      controller.reload();
    }
  };

  const renderCustomHeaderIcons = () => (
    <>
      <ExternalFilter
        appliedFilters={appliedFilters}
        controller={controller}
        setAppliedfilters={setAppliedfilters}
        onSearch={onSearch}
        setSelectedOptions={setSelectedOptions}
        setSearchData={setSearchData}
      />
      <button
        type="button"
        data-id={`${ATC.PACKETID}REFRESH${ATC.BUTTON}`}
        onClick={() => {
          const prevAppliedFilter = { ...appliedFilters };
          controller.filterExternally(prevAppliedFilter, false, false);
        }}
        className="fn-btn turquoise"
        title="Refresh Grid"
      >
        <FontAwesomeIcon icon={faSyncAlt} />
      </button>
    </>
  );

  const onFilter = (col, filter) => {
    const prevAppliedFilter = { ...appliedFilters };
    if (filter.value) {
      prevAppliedFilter[filter.field] = filter.value;
    } else if (!filter.value && Object.keys(prevAppliedFilter).indexOf(filter.field) >= 0) {
      delete prevAppliedFilter[filter.field];
    }
    setAppliedfilters(prevAppliedFilter);
  };

  const warningEditCheck = (isGridActionBtnClick, index) => {
    if (isGridActionBtnClick || selectedIndex !== index) {
      openConfirmation({
        header: `Warning`,
        message: `${selectedPacket.packetId} is in edit state. Please either save, or discard the changes you have made before moving on.`,
        secondBtn: 'Close'
      });
      controller.highlightRow(prevSelectedIndex);
    }
  };

  const toggleLockBtnHandler = (data, isTake = false) => {
    const { username } = session;
    const lockedBy = data.lockedBy === username ? null : username;
    let postBody;
    if (!isChildEdit && !isChildAddEdit) {
      if (isTake) {
        postBody = {
          ...data,
          lockedBy,
          status: 'IN_PROGRESS',
          assignedTo: username
        };
      } else {
        postBody = {
          ...data,
          lockedBy
        };
      }

      bindLogger(
        togglePacketLockBtn1,
        context
      )(postBody).subscribe(() => {
        showSuccessNotification(`Record ${lockedBy != null ? 'Locked' : 'Unlocked'} for ${data.packetId}`);

        if (isTake) {
          const newFilter = {
            packetId: data.packetId
          };
          const historyData = {
            followupReason: null,
            pktId: data.packetId,
            status: 'IN_PROGRESS',
            trackingBarcode: data.trackingBarCode
          };
          setAllowEditing(true);
          setIsTakeBtn(true);
          setPage(0);
          setTakeComm(true);
          bindLogger(saveEventHistory1, context)(data.packetId, historyData).subscribe((res) => {
            console.log('resp: ', res);
          });
          setTimeout(() => {
            controller.filterExternally(newFilter, false, true);
            setAditionalDetailsEdit(true);
          }, 1000);
          setAppliedfilters(newFilter);
        } else {
          controller.filterExternally(appliedFilters, false, false);
        }
      });
    } else {
      // calling the warning dialogue for the action button click when bottom section details are in edit mode
      warningEditCheck(true);
    }
  };

  const onRowClick = (row, index, meta, event, currentRowPage) => {
    if (!isChildEdit && !isChildAddEdit) {
      setSelectedPacket(row);
      setPage(currentRowPage);
      setIndexing1();
      bindLogger(clearTrackingCodeStatus1, context)();
      if (row != null && row.packetId != null && row.object != null) {
        setSelectedindex(index);
        controller.highlightRow(index);
        toggleAdditionalDetails1(isAditionalDetailsEdit);
        setAditionalDetailsEdit(false);
        bindLogger(getTrackingCodeStatus1, context)(row.trackingBarCode);
        bindLogger(getIndexing1, context)(row.packetId, row.object);
        bindLogger(getPacketCommunication1, context)(row.packetId);
        // changed ENROLLMENT_TRANSFER to PAPER_CRFS_QUERIES in setAllowEditing <--- discussed on 9/3/21 --->
        // setAllowEditing is being used by indexing grid and additional details
        // Removed other logics <-- previous logic was used for process button,
        // since we dont have a process button anymore, using the below logic <--- discussed on 10/3/21 --->
        setAllowEditing(row.lockedBy === session.username);
        setIsTakeBtn(false);
        setTakeComm(false);
      }
    } else {
      // first parameter to check any grid acttion button clicked,
      // second parameter pass the current selected index
      warningEditCheck(false, index);
    }
  };

  const onDataLoad = (response, currentPage) => {
    const { data } = response;
    if (page > 0) {
      if (isTakeBtn) {
        controller.setPage(currentPage);
      } else {
        controller.setPage(page > 0 && currentPage === 0 ? page : currentPage);
      }
      setPage(currentPage);
    }
    setMetadata(response.meta);
    if (data != null && data.length > 0) {
      const index = data.length > selectedIndex && selectedIndex !== -1 ? selectedIndex : 0;
      onRowClick(data[index], index, {}, {}, page);
    } else {
      onRowClick(null, 0, {}, {}, 0);
    }
  };

  const onClearFilter = () => {
    setAppliedfilters({});
  };

  return (
    <RenderControl compName="Packet Browser" permission={CanUserViewDetails()}>
      <RenderCardHeader>
        <ContentHeader title="Packet Processing" ariaLevel="2" />
      </RenderCardHeader>
      <RenderCardBody className="mb-2">
        <RenderElement access={CanViewExternalFilters()}>
          <Filters
            onSearch={onSearch}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            searchData={searchData}
            onClearExternalFilter={onClearExternalFilter}
            setSearchData={setSearchData}
          />
        </RenderElement>
        <div className="packet-grid mb-2">
          <GridTable
            id="packetId"
            tag={`${ATC.PACKETID}BROWSER${ATC.TABLE}`}
            key="packetId"
            user={session.username}
            remoteUrl="regulatory-data-service/v1/packets/browse"
            onFilter={onFilter}
            controller={controller}
            columns={PacketBrowserColumns}
            rowEvents={[toggleLockBtnHandler]}
            deletable={false}
            sortable
            onClearFilter={onClearFilter}
            rowEditable={() => true}
            rowDeletable={() => false}
            recordPerPage={10}
            renderCustomHeaderIcons={renderCustomHeaderIcons}
            filterable
            showFilterByDefault
            enableToolbar
            rowsPerPageOptions={[10, 20, 100]}
            getToken={getToken}
            highlightRow={0}
            filterState={filterVal}
            onDataLoad={onDataLoad}
            onRowClicked={onRowClick}
            defaultSorts={[
              {
                field: 'receivedDate',
                dir: 'asc'
              }
            ]}
            exportTitle={getExportTitle('Packet Processing Browser')}
            toPdf={false}
          />
        </div>
        {selectedPacket != null ? (
          <div className="row flex-nowrap">
            <div className="col-7 mr-4">
              <RenderElement access={CanUserViewIndex()}>
                <Indexing
                  packet={selectedPacket}
                  controller={controller}
                  allowEditing={allowEditing}
                  appliedFilters={appliedFilters}
                  rowClick={onRowClick}
                  selectedGridIndex={selectedIndex}
                  history={history}
                  isChildEdit={isChildEdit}
                  setIsChildEdit={setIsChildEdit}
                  setPrevSelectedIndex={setPrevSelectedIndex}
                />
              </RenderElement>
            </div>
            <div className="col-5">
              <AdditionalDetails
                packet={selectedPacket}
                metaData={metaData}
                controller={controller}
                allowEditing={allowEditing}
                rowClick={onRowClick}
                appliedFilters={appliedFilters}
                selectedIndex={selectedIndex}
                setSelectedindex={setSelectedindex}
                setPrevSelectedIndex={setPrevSelectedIndex}
                takeComm={takeComm}
                setTakeComm={setTakeComm}
                isChildEdit={isChildAddEdit}
                setIsChildEdit={setIsChildAddEdit}
              />
            </div>
          </div>
        ) : null}
      </RenderCardBody>
    </RenderControl>
  );
}

const withConnectState = connect(null, (dispatch) => bindActionCreators(
  {
    togglePacketLockBtn1: togglePacketLockBtn,
    getIndexing1: getIndexing,
    getTrackingCodeStatus1: getTrackingCodeStatus,
    clearTrackingCodeStatus1: clearTrackingCodeStatus,
    getPacketCommunication1: getPacketCommunication,
    setIndexing1: setIndexing,
    toggleAdditionalDetails1: toggleAdditionalDetails,
    saveEventHistory1: saveEventHistory
  },
  dispatch
));
export default withAppContext(withConnectState(PacketBrowser));
